import { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { STATIC_NAV_ITEMS, fetchCmsNavigation } from '../lib/cms';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navItems, setNavItems] = useState(STATIC_NAV_ITEMS);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    fetchCmsNavigation().then((items) => {
      if (items && items.length > 0) {
        // Enforce sub-items if present
        setNavItems(items);
      }
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-maritime-950/95 backdrop-blur-md shadow-lg border-b border-maritime-800/80 py-3'
          : 'bg-maritime-950/80 backdrop-blur-sm border-b border-white/10 py-4 sm:py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3.5 group focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded-md"
            aria-label="JT transportsejlads forside"
          >
            <img
              src="https://assets.auxodev.dk/assets/a8b5f246-9eaa-4cb1-adf9-4b882abefbbd/logo.png"
              alt="JT transportsejlads"
              className={`w-auto object-contain brightness-0 invert transition-all duration-300 ${
                scrolled ? 'h-9 sm:h-11' : 'h-14 sm:h-16 md:h-20'
              }`}
            />
          </Link>

          {/* Desktop Navigation (Visible ONLY at 1200px and above) */}
          <nav className="hidden min-[1200px]:flex items-center gap-1.5" aria-label="Hovednavigation">
            {navItems.map((item) => (
              <NavLink
                key={item.url || item.label}
                to={item.url}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-maritime-800 text-white font-semibold'
                      : 'text-slate-200 hover:text-white hover:bg-white/10'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Desktop Quick Contacts & CTA (1200px and above) */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            <a
              href="tel:+4550510062"
              className="flex items-center gap-2 text-sm font-medium text-slate-200 hover:text-cyan-400 transition-colors whitespace-nowrap"
              title="Ring til JT transportsejlads"
            >
              <i className="fa-solid fa-phone text-cyan-400" aria-hidden="true" />
              <span className="whitespace-nowrap font-medium">+45 50 51 00 62</span>
            </a>

            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold shadow-md hover:shadow-cyan-500/20 transition-all focus:outline-none focus:ring-2 focus:ring-cyan-400"
            >
              <span>Få et tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Mobile / Tablet Controls (Visible ONLY below 1200px) */}
          <div className="flex min-[1200px]:hidden items-center gap-2.5">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2.5 rounded-lg text-slate-200 hover:text-white hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400"
              aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn navigation'}
              aria-expanded={mobileMenuOpen}
            >
              <i
                className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay & Drawer (Below 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-maritime-950 border-b border-maritime-800 px-4 pt-3 pb-6 space-y-3 shadow-2xl animate-in slide-in-from-top duration-200">
          <nav className="flex flex-col space-y-1" aria-label="Mobil navigation">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `px-4 py-2.5 rounded-lg text-base font-medium transition-colors ${
                  isActive
                    ? 'bg-maritime-800 text-cyan-300 font-semibold'
                    : 'text-slate-200 hover:bg-white/10'
                }`
              }
            >
              Forside
            </NavLink>

            <NavLink
              to="/baadtransport"
              className={({ isActive }) =>
                `px-4 py-2.5 rounded-lg text-base font-medium transition-colors ${
                  isActive
                    ? 'bg-maritime-800 text-cyan-300 font-semibold'
                    : 'text-slate-200 hover:bg-white/10'
                }`
              }
            >
              Bådtransport
            </NavLink>

            <NavLink
              to="/referencer"
              className={({ isActive }) =>
                `px-4 py-2.5 rounded-lg text-base font-medium transition-colors ${
                  isActive
                    ? 'bg-maritime-800 text-cyan-300 font-semibold'
                    : 'text-slate-200 hover:bg-white/10'
                }`
              }
            >
              Referencer
            </NavLink>

            <NavLink
              to="/om-mig"
              className={({ isActive }) =>
                `px-4 py-2.5 rounded-lg text-base font-medium transition-colors ${
                  isActive
                    ? 'bg-maritime-800 text-cyan-300 font-semibold'
                    : 'text-slate-200 hover:bg-white/10'
                }`
              }
            >
              Om mig
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-4 py-2.5 rounded-lg text-base font-medium transition-colors ${
                  isActive
                    ? 'bg-maritime-800 text-cyan-300 font-semibold'
                    : 'text-slate-200 hover:bg-white/10'
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>

          <div className="pt-3 border-t border-maritime-800/80 flex flex-col gap-2.5">
            <a
              href="tel:+4550510062"
              className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg bg-white/10 text-white font-medium text-sm hover:bg-white/15 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-cyan-400" aria-hidden="true" />
              <span className="whitespace-nowrap">+45 50 51 00 62</span>
            </a>

            <a
              href="mailto:johann2@hotmail.com"
              className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg bg-white/5 text-slate-300 text-sm hover:text-white transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-cyan-400" aria-hidden="true" />
              <span className="whitespace-nowrap">johann2@hotmail.com</span>
            </a>

            <Link
              to="/kontakt"
              className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-lg bg-cyan-600 text-white font-semibold text-sm shadow-md hover:bg-cyan-500 transition-all"
            >
              <span>Kontakt mig i dag</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
